(() => {
  if (customElements.get('blog-filter-results')) return;

  class BlogFilterResults extends HTMLElement {
    connectedCallback() {
      console.info('[ArticleBlogFilter][8] Filter results connected', {
        pathname: window.location.pathname,
        filterIsActive: this.filterIsActive,
        resultCards: this.querySelectorAll('.blog-filter-card').length,
      });
      this.onClickHandler = this.onClick.bind(this);
      this.addEventListener('click', this.onClickHandler);
      this.syncPageSections();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClickHandler);
      this.abortController?.abort();
    }

    get filterIsActive() {
      return this.dataset.filterActive === 'true';
    }

    isFirstArticleRowVisible() {
      const cardSelector = [
        '.blog-filter-card',
        '.blog-section-card',
        '.blog-carousel-card',
        '.blog-grid-card',
        '.blog-cover-card',
      ].join(',');
      const visibleCards = Array.from(document.querySelectorAll(cardSelector))
        .map((card) => ({ card, rect: card.getBoundingClientRect() }))
        .filter(({ rect }) => rect.width > 0 && rect.height > 0);

      if (!visibleCards.length) return false;

      const firstRowTop = visibleCards[0].rect.top;
      const firstRow = visibleCards.filter(({ rect }) => Math.abs(rect.top - firstRowTop) <= 2);
      const viewportTop = this.getStickyOffset();

      return firstRow.some(({ rect }) => rect.bottom > viewportTop && rect.top < window.innerHeight);
    }

    getStickyOffset() {
      const stickyHeader = document.querySelector(
        '.section-header.shopify-section-header-sticky:not(.shopify-section-header-hidden)',
      );
      const blogToolbar = document.querySelector('[data-toolbar].is-fixed');

      return (
        (stickyHeader?.getBoundingClientRect().height || 0) +
        (blogToolbar?.getBoundingClientRect().height || 0) +
        16
      );
    }

    getScrollTarget() {
      if (this.filterIsActive) return this;

      const firstArticleSection = document.querySelector(
        'blog-feature-section, blog-section-carousel, blog-section-grid, blog-section-cover-carousel',
      );

      return firstArticleSection?.closest('.shopify-section') || firstArticleSection || this;
    }

    syncPageSections() {
      const selectors = [
        'blog-feature-section',
        'blog-section-carousel',
        'blog-section-grid',
        'blog-section-cover-carousel',
        '.main-blog',
      ];

      const syncedSections = [];

      document.querySelectorAll(selectors.join(',')).forEach((element) => {
        const section = element.closest('.shopify-section') || element;
        if (section.contains(this)) return;
        section.classList.toggle('is-hidden-by-blog-filter', this.filterIsActive);
        syncedSections.push({
          id: section.id || null,
          element: element.tagName.toLowerCase(),
          hidden: section.classList.contains('is-hidden-by-blog-filter'),
        });
      });

      console.info('[ArticleBlogFilter][9] All/result sections synchronized', {
        pathname: window.location.pathname,
        filterIsActive: this.filterIsActive,
        sections: syncedSections,
      });
    }

    onClick(event) {
      const paginationLink = event.target.closest('.pagination__item[href]');
      if (!paginationLink || !this.contains(paginationLink)) return;

      event.preventDefault();
      event.stopPropagation();

      this.renderUrl(paginationLink.href, { scrollToResults: true }).catch((error) => {
        if (error.name !== 'AbortError') window.location.assign(paginationLink.href);
      });
    }

    async renderUrl(url, { scrollToResults = false } = {}) {
      const requestUrl = new URL(url, window.location.origin);
      const isTaggedUrl = requestUrl.pathname.includes('/tagged/');
      const hasCuratedShell = Boolean(document.querySelector('[data-blog-curated-shell]'));

      console.info('[ArticleBlogFilter][10] AJAX render requested', {
        currentPath: window.location.pathname,
        requestPath: requestUrl.pathname,
        isTaggedUrl,
        hasCuratedShell,
        scrollToResults,
      });

      // Tagged SSR leaves curated sections empty; returning to All needs a full reload.
      if (!isTaggedUrl && hasCuratedShell) {
        console.info('[ArticleBlogFilter][11] Returning to All with a full navigation', { url });
        window.location.assign(url);
        return;
      }

      this.abortController?.abort();
      this.abortController = new AbortController();

      requestUrl.searchParams.set('section_id', this.dataset.sectionId);
      this.setAttribute('aria-busy', 'true');

      try {
        const response = await fetch(requestUrl.toString(), {
          credentials: 'same-origin',
          signal: this.abortController.signal,
        });

        if (!response.ok) throw new Error(`Blog request failed: ${response.status}`);
        console.info('[ArticleBlogFilter][11] AJAX response received', {
          requestUrl: requestUrl.toString(),
          status: response.status,
        });

        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const newResults = html.querySelector('blog-filter-results');
        if (!newResults) throw new Error('Blog filter results were not returned.');

        this.replaceWith(newResults);
        console.info('[ArticleBlogFilter][12] AJAX results replaced', {
          filterIsActive: newResults.dataset.filterActive,
          resultCards: newResults.querySelectorAll('.blog-filter-card').length,
        });

        if (scrollToResults) {
          requestAnimationFrame(() => {
            const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
            const scrollTarget = newResults.getScrollTarget();
            const top = window.scrollY + scrollTarget.getBoundingClientRect().top - newResults.getStickyOffset();

            window.scrollTo({ top: Math.max(0, top), behavior: reducedMotion ? 'auto' : 'smooth' });
          });
        }

        return newResults;
      } finally {
        if (this.isConnected) this.removeAttribute('aria-busy');
      }
    }
  }

  customElements.define('blog-filter-results', BlogFilterResults);
})();
